import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, CardBody, CardHeader, Col, Container, Form, FormGroup, Input, Label, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import Swal from "sweetalert2";
import { clientActions } from "../../sagas/clientSlice";
import Select from "react-select";
import { productActions } from "../../sagas/productSlice";
import * as Yup from 'yup'; // Import Yup for validation
import ClientArticles from "../Client/ClientArticles";
import "../shared/DocumentForm.css";
import "../Reception/SimpleReception.css";
import PurchaseDiscount from "../shared/PurchaseDiscount";
import { entrepotActions } from "../../sagas/entrepotSlice";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { deliveryActions } from "../../sagas/deliveryNoteSlice";
import { formatPrice } from "../../helpers/utility";
import { reglementActions } from "../../sagas/reglementSlice";
import { FaArrowDown, FaArrowUp, FaBalanceScale, FaCalendarAlt, FaShieldAlt } from "react-icons/fa";
import { commercialActions } from "../../sagas/commercialSlice";

const initForm = {
    clientId: null,
    chantierId: null,
    lignes: [],
    dateLivraison: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    dateOperation: new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
    addresseLivraison: '',
    totalAmountHt: 0,
    totalAmountTtc: 0,
    totalAmountTva: 0,
    status: 'LIVRE',
    entrepotId: null,
    transporteurId: null,
    modeReglement: "-1",
};

const BonLivraisonForm = ({ onClose, bonLivraison }) => {
    const { t } = useTranslation('translation');
    const [formState, setForm] = useState(initForm);
    const dispatch = useDispatch();
    const clients = useSelector(state => state.clients.clients);
    const products = useSelector(state => state.products.products);
    const [currentPage, setCurrentPage] = useState(1);
    const { entrepots } = useSelector(state => state.entrepots);
    const { transporteurs } = useSelector(state => state.transporteurs);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials); // Adjust state path as needed

    const isClosed = bonLivraison && bonLivraison.status === 'LIVRE';

    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });

    useEffect(() => {
        if (bonLivraison) {
            setForm({ ...bonLivraison });
        } else {
            setForm(initForm);
        }

        dispatch(entrepotActions.list());
        dispatch(transporteurActions.list());
    }, [bonLivraison]);

    useEffect(() => {
        dispatch(commercialActions.list({ query: '' })); // Fetch commercials on component mount
    }, []);

    const commercialOptions = commercialList?.map(com => ({
        value: com.id,
        label: com.fullName,
    })) ?? [];

    // Validation schema
    const validationSchema = Yup.object().shape({
        clientId: Yup.number()
            .required(t('bonLivraison.clientRequired')), // Ensure a client is selected
        entrepotId: Yup.number()
            .required(t('bonLivraison.entrepotRequired')), // Ensure an entrepot is selected
        // transporteurId: Yup.number()
        //     .required(t('bonLivraison.transporteurRequired')), // Ensure a transporteur is selected
        modeRemise: Yup.string().oneOf(['MONTANT', 'POURCENTAGE']),
        valeurRemise: Yup.number().min(0).required().test('discount-limit', 'La remise ne peut pas dépasser 100 %.', function (value) { return this.parent.modeRemise !== 'POURCENTAGE' || value <= 100; }),
        lignes: Yup.array().min(1, t('bonLivraison.atLeastOneItem')).test('valid-lines', 'Chaque article doit avoir une quantité positive et un prix valide.', lines => (lines || []).every(line => Number(line.quantiteLivree) > 0 && Number.isFinite(Number(line.quantiteLivree)) && line.prixUnitaireTTC !== '' && line.prixUnitaireTTC != null && Number.isFinite(Number(line.prixUnitaireTTC)) && Number(line.prixUnitaireTTC) >= 0)), // Ensure at least one item is added

    });

    const formik = useFormik({
        initialValues: {
            ...formState,
            modeRemise: formState.modeRemise || 'MONTANT',
            valeurRemise: formState.valeurRemise ?? formState.montantRemise ?? 0,
            montantRemise: Number(formState.montantRemise || 0),
            montantTotalAvantRemise: Number(formState.montantTotalAvantRemise ?? (Number(formState.montantTotalTTC || 0) + Number(formState.montantRemise || 0))),
            clientId: bonLivraison ? bonLivraison.clientId : null,
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {

            Swal.fire({
                title: t('purchase.confirmation'),
                text: t('purchase.saveOrConfirmText'),
                icon: 'question',
                showCancelButton: true,
                showDenyButton: true,
                confirmButtonText: t('common.saveAndConfirm'),
                denyButtonText: t('common.save'),
                cancelButtonText: t('common.cancel'),

            }).then((result) => {
                if (result.isConfirmed) {
                    if (bonLivraison) {
                        let payload = {
                            id: bonLivraison.id,
                            data: { ...values, status:"LIVRE", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.updated'),
                                    text: t('bonLivraison.updatedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.update(payload));
                    } else {
                        let payload = {
                            data: { ...values, status:"LIVRE", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.saved'),
                                    text: t('bonLivraison.savedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.addBL(payload));
                    }
                } else if (result.isDenied) {                
                    if (bonLivraison) {
                        let payload = {
                            id: bonLivraison.id,
                            data: { ...values, status:"EN_PREPARATION", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.updated'),
                                    text: t('bonLivraison.updatedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.update(payload));
                    } else {
                        let payload = {
                            data: { ...values, status:"EN_PREPARATION", modeReglement: values.modeReglement === "-1" ? null : values.modeReglement },
                            onSuccess: () => {
                                Swal.fire({
                                    icon: 'success',
                                    title: t('common.saved'),
                                    text: t('bonLivraison.savedSuccess'),
                                });
                                onClose();
                            },
                        };
                        dispatch(deliveryActions.addBL(payload));
                    }
                } else {

                }
            });


        },
    });




    useEffect(() => {
        // Charger la liste des clients si elle n'est pas déjà disponible

        if (!clients || clients.length === 0) {
            dispatch(clientActions.search({
                query: ""
            }));
        }

        if (!products || products.length === 0) {
            dispatch(productActions.findAll({
                search: "",
                page: currentPage - 1,
                size: 10,
            }));
        }

        if (clients && bonLivraison && selectedClient == null) {
            setForm({ ...bonLivraison });
            if (bonLivraison.chantierNom && bonLivraison.clientId) {
                const client = clients.find(c => c.id === bonLivraison.clientId);
                if (client) {
                    const chantier = client.chantiers.find(ch => ch.nom === bonLivraison.chantierNom);
                    setSelectedChantier(chantier ? { value: chantier.id, label: chantier.nom } : null);
                    setSelectedClient({
                        value: client.id,
                        label: client.typeClient === 'PARTICULIER' ? `${client.nom} ${client.prenom || ''}` : client.entreprise,
                        chantiers: client.chantiers,
                        solde: client.solde,
                        totalDebit: client.totalDebit,
                        totalCredit: client.totalCredit,
                    });

                }
            }
        }

    }, [bonLivraison, clients, dispatch]);

    useEffect(() => {
        if (clients && bonLivraison) {
            let client = clients.find(c => c.id === bonLivraison.clientId);
            dispatch(reglementActions.echeanceClient({ ...filters, clientId: client.id }));
            dispatch(reglementActions.cautionClient({ ...filters, clientId: client.id }));
        }
    }, [clients, bonLivraison])

    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));
    }, [selectedClient])


    const { values, getFieldProps, handleSubmit, setFieldValue, errors, touched } = formik;

    useEffect(() => {
        let client = clients.find(client => client.id === values.clientId);
        setFieldValue('addresseLivraison', client ? client.adresse : '');
    }, [values.clientId]);

    // Map clients to options for React-Select
    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        chantiers: client.chantiers || [],
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom}`
            : client.entreprise,
    })) ?? [];

    const entrepotOptions = entrepots?.map(entrepot => ({
        value: entrepot.id,
        label: entrepot.code + ' - ' + entrepot.libelle,
    })) ?? [];

    const transporteurOptions = transporteurs?.map(transporteur => ({
        value: transporteur.id,
        label: transporteur.code + ' - ' + transporteur.libelle,
    })) ?? [];

    // Calculate montantTotalTaxe and montantTotalTTC
    const montantTotalTaxe = values.montantTotalTaxe || 0;
    const montantTotalTTC = values.montantTotalTTC || 0;
    const montantTotalHT = values.montantTotalHT || 0;

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    return (
        <Container fluid className="document-form-page simple-reception">
            <Form onSubmit={handleSubmit} onAbort={onClose}>
                <Card className="document-form-card">
                    <CardHeader className="document-form-header">
                        <div className="receipt-step-title"><span>1</span><h2>Pour quel client ?</h2></div>
                        <Row className="g-3">
                            <Col md={6}><Label for="sales-client">Client *</Label>
                                <Select inputId="sales-client" options={clientOptions} value={clientOptions.find(option => option.value === values.clientId) || null}
                                    onChange={async (selectedOption) => {
                                        const nextClientId = selectedOption?.value ?? null;
                                        if (String(nextClientId) === String(values.clientId)) return;
                                        if (values.lignes.length) {
                                            const result = await Swal.fire({
                                                icon: 'warning', title: 'Changer de client ?',
                                                text: 'Les articles saisis seront retirés pour charger le catalogue du nouveau client.',
                                                showCancelButton: true, confirmButtonText: 'Changer de client', cancelButtonText: 'Conserver',
                                            });
                                            if (!result.isConfirmed) return;
                                        }
                                        formik.setValues(previous => ({ ...previous, clientId: nextClientId, chantierId: null,
                                            lignes: [], montantTotalHT: 0, montantTotalTaxe: 0, montantTotalTTC: 0, montantTotalAvantRemise: 0, montantRemise: 0, valeurRemise: 0 }));
                                        setSelectedClient(selectedOption);
                                        setSelectedChantier(null);
                                        if (nextClientId) {
                                            dispatch(reglementActions.echeanceClient({ ...filters, clientId: nextClientId }));
                                            dispatch(reglementActions.cautionClient({ ...filters, clientId: nextClientId }));
                                        }
                                        setFilters(f => ({ ...f, clientId: nextClientId }));
                                    }}
                                    placeholder="Choisir le client…" isSearchable isDisabled={isClosed} />
                                {touched.clientId && errors.clientId && <div className="text-danger">{errors.clientId}</div>}
                            </Col>
                            <Col md={3}><Label for="sales-warehouse">Entrepôt *</Label>
                                <Select inputId="sales-warehouse" options={entrepotOptions} value={entrepotOptions.find(option => option.value === values.entrepotId) || null}
                                    onChange={option => setFieldValue('entrepotId', option?.value ?? null)} placeholder="Choisir…" isDisabled={isClosed} />
                                {touched.entrepotId && errors.entrepotId && <div className="text-danger">{errors.entrepotId}</div>}
                            </Col>
                            <Col md={3}><Label for="sales-date">Date de livraison</Label><Input id="sales-date" type="date" {...getFieldProps('dateLivraison')} disabled={isClosed} /></Col>
                        </Row>
                        <details className="receipt-optional"><summary>Autres informations : paiement, adresse de livraison…</summary>
                            <Row className="g-3">
                                <Col md={6}><Label for="sales-operation">Date comptable</Label><Input id="sales-operation" type="date" {...getFieldProps("dateOperation")} disabled={isClosed} /></Col>
                                <Col md={6}><Label for="sales-payment">Mode de paiement</Label>
                                    <Input id="sales-payment" type="select" value={values.modeReglement || ''} onChange={event => setFieldValue('modeReglement', event.target.value || null)} disabled={isClosed}>
                                        <option value="">Sélectionner</option><option value="ESPECES">Espèces</option><option value="CHEQUE">Chèque</option><option value="VIREMENT">Virement</option><option value="CARTE_BANCAIRE">Carte bancaire</option><option value="TRAITE">Traite</option>
                                    </Input>
                                </Col>
                                <Col xs={12}><Label for="sales-note">Adresse de livraison</Label><Input id="sales-note" type="textarea" rows={2} {...getFieldProps('addresseLivraison')} disabled={isClosed} /></Col>
                            </Row>
                        </details>
                    </CardHeader>
                    <CardBody className="document-form-body">
                        <div className="receipt-step-title"><span>2</span><h2>Quels articles ?</h2></div>
                        <Row>
                            <ClientArticles formik={formik} clientId={values.clientId} quantityField="quantiteLivree" disabled={isClosed} />
                        </Row>
                        {errors.lignes && touched.lignes && (
                            <div className="text-danger small mt-1">{errors.lignes}</div>
                        )}
                        <section className="document-form-section">
                            <div className="receipt-step-title"><span>3</span><h2>Remise et total</h2></div>
                            <PurchaseDiscount formik={formik} disabled={isClosed} label="Remise du client" />
                            {touched.valeurRemise && errors.valeurRemise && <div className="text-danger">{errors.valeurRemise}</div>}
                            <Row className="g-3 text-end">
                                <Col sm={4}><small>Total HT avant remise</small><div>{formatPrice(montantTotalHT)}</div></Col>
                                <Col sm={4}><small>TVA avant remise</small><div>{formatPrice(montantTotalTaxe)}</div></Col>
                                <Col sm={4}><small>Total TTC avant remise</small><div>{formatPrice(values.montantTotalAvantRemise)}</div></Col>
                            </Row>
                            <div className="receipt-checkout-total"><div>Net à payer<div className="text-muted">{values.lignes.length} article(s) · taxes comprises</div></div><strong>{formatPrice(montantTotalTTC)}</strong></div>
                            <div className="receipt-checkout-actions">
                                <Button type="submit" color="primary" disabled={isClosed}>Enregistrer la livraison</Button>
                                <Button type="button" color="link" onClick={onClose}>Retour</Button>
                            </div>
                        </section>
                    </CardBody>
                </Card>
            </Form>
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};
export default BonLivraisonForm;